import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { CreateAdminUserModal } from "./CreateAdminUserModal";
import { EditAdminUserModal } from "./EditAdminUserModal";
import { DeleteAdminUserModal } from "./DeleteAdminUserModal";
import { Skeleton } from "@/components/ui/skeleton";

interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
}

export function AdminUsersManagement() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    loadAdminUsers();
  }, []);

  const loadAdminUsers = async () => {
    try {
      setLoading(true);

      // Buscar todos os user_ids com role 'admin'
      const { data: adminRoles } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'admin');

      const adminUserIds = adminRoles?.map(r => r.user_id) || [];

      if (adminUserIds.length === 0) {
        setUsers([]);
        return;
      }

      // Buscar profiles dos admins
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, created_at')
        .in('id', adminUserIds);

      // Buscar dados de auth via edge function
      const { data: authData, error: authError } = await supabase.functions.invoke(
        'admin-get-users',
        { body: { userIds: adminUserIds } }
      );

      // Log de debug
      console.log('Admin users response:', { authData, authError });

      if (authError) {
        console.error('Erro ao buscar dados de auth:', authError);
        toast.error('Erro ao buscar emails dos usuários');
        // Ainda assim mostrar os usuários com dados de profiles
      }

      // Combinar dados (mesmo se authData falhar, mostra pelo menos profiles)
      const combinedUsers = profiles?.map(profile => {
        const authUser = authData?.users?.find((u: { id: string; email?: string; last_sign_in_at?: string }) => u.id === profile.id);
        return {
          id: profile.id,
          email: authUser?.email || 'Email não disponível',
          full_name: profile.full_name,
          created_at: profile.created_at,
          last_sign_in_at: authUser?.last_sign_in_at || null
        };
      }) || [];

      setUsers(combinedUsers);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários admin');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Usuários Administradores</CardTitle>
          <CardDescription>Carregando...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Usuários Administradores</CardTitle>
              <CardDescription>
                Gerencie os usuários com acesso ao painel administrativo
              </CardDescription>
            </div>
            <Button onClick={() => setCreatingUser(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Novo Usuário Admin
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum usuário administrador encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead>Último acesso</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>{format(new Date(user.created_at), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>
                      {user.last_sign_in_at
                        ? format(new Date(user.last_sign_in_at), 'dd/MM/yyyy HH:mm')
                        : 'Nunca'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingUser(user)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setDeletingUser(user)}
                          disabled={users.length === 1}
                          title={users.length === 1 ? 'Não é possível excluir o último admin' : ''}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateAdminUserModal
        open={creatingUser}
        onOpenChange={setCreatingUser}
        onSuccess={() => {
          setCreatingUser(false);
          loadAdminUsers();
        }}
      />

      {editingUser && (
        <EditAdminUserModal
          open={!!editingUser}
          onOpenChange={(open) => !open && setEditingUser(null)}
          user={editingUser}
          onSuccess={() => {
            setEditingUser(null);
            loadAdminUsers();
          }}
        />
      )}

      {deletingUser && (
        <DeleteAdminUserModal
          open={!!deletingUser}
          onOpenChange={(open) => !open && setDeletingUser(null)}
          user={deletingUser}
          onSuccess={() => {
            setDeletingUser(null);
            loadAdminUsers();
          }}
        />
      )}
    </>
  );
}
